import api from './config';

/**
 * API Client para módulo de Calendario Laboral
 */
export const calendarAPI = {
  // ============================================================================
  // Configuración de días laborables
  // ============================================================================

  /**
   * Obtener configuración de días laborables
   * @param {string} departmentId - ID del departamento (opcional)
   * @returns {Promise} Configuración de días laborables
   */
  getConfig: async (departmentId = null) => {
    const params = departmentId ? { departmentId } : {};
    const response = await api.get('/calendar/config', { params });
    return response.data;
  },

  /**
   * Guardar configuración de días laborables
   * @param {Object} data - Datos de configuración
   * @returns {Promise} Configuración guardada
   */
  saveConfig: async (data) => {
    const response = await api.post('/calendar/config', data);
    return response.data;
  },

  // ============================================================================
  // Feriados
  // ============================================================================

  /**
   * Obtener lista de eventos/feriados con filtros (alias para compatibilidad)
   * @param {Object} filters - Filtros (anio, mes, tipo)
   * @returns {Promise} Lista de eventos
   */
  getEvents: async (filters = {}) => {
    const response = await api.get('/calendar/events', { params: filters });
    return response.data;
  },

  /**
   * Obtener lista de feriados con filtros
   * @param {Object} filters - Filtros (anio, tipo, includeNational)
   * @returns {Promise} Lista de feriados
   */
  getHolidays: async (filters = {}) => {
    const response = await api.get('/calendar/holidays', { params: filters });
    return response.data;
  },

  /**
   * Crear nuevo evento (alias para compatibilidad)
   * @param {Object} data - Datos del evento
   * @returns {Promise} Evento creado
   */
  createEvent: async (data) => {
    const response = await api.post('/calendar/events', data);
    return response.data;
  },

  /**
   * Crear nuevo feriado
   * @param {Object} data - Datos del feriado
   * @returns {Promise} Feriado creado
   */
  createHoliday: async (data) => {
    const response = await api.post('/calendar/holidays', data);
    return response.data;
  },

  /**
   * Actualizar evento (alias para compatibilidad)
   * @param {string} id - ID del evento
   * @param {Object} data - Datos actualizados
   * @returns {Promise} Evento actualizado
   */
  updateEvent: async (id, data) => {
    const response = await api.put(`/calendar/events/${id}`, data);
    return response.data;
  },

  /**
   * Actualizar feriado existente
   * @param {string} id - ID del feriado
   * @param {Object} data - Datos actualizados
   * @returns {Promise} Feriado actualizado
   */
  updateHoliday: async (id, data) => {
    const response = await api.put(`/calendar/holidays/${id}`, data);
    return response.data;
  },

  /**
   * Eliminar evento (alias para compatibilidad)
   * @param {string} id - ID del evento
   * @returns {Promise} Confirmación
   */
  deleteEvent: async (id) => {
    const response = await api.delete(`/calendar/events/${id}`);
    return response.data;
  },

  /**
   * Eliminar feriado (soft delete)
   * @param {string} id - ID del feriado
   * @returns {Promise} Confirmación
   */
  deleteHoliday: async (id) => {
    const response = await api.delete(`/calendar/holidays/${id}`);
    return response.data;
  },

  /**
   * Copiar feriados de un año a otro
   * @param {number} anioOrigen - Año de origen
   * @param {number} anioDestino - Año de destino
   * @returns {Promise} Feriados copiados
   */
  bulkCopyHolidays: async (anioOrigen, anioDestino) => {
    const response = await api.post('/calendar/holidays/bulk', { anioOrigen, anioDestino });
    return response.data;
  },

  // ============================================================================
  // Utilidades
  // ============================================================================

  /**
   * Calcular días laborables entre dos fechas
   * @param {string} fechaInicio - Fecha inicial (YYYY-MM-DD)
   * @param {string} fechaFin - Fecha final (YYYY-MM-DD)
   * @param {string} departmentId - ID del departamento (opcional)
   * @returns {Promise} Número de días laborables
   */
  calcularDiasLaborables: async (fechaInicio, fechaFin, departmentId = null) => {
    const params = { fechaInicio, fechaFin };
    if (departmentId) params.departmentId = departmentId;
    const response = await api.get('/calendar/dias-laborables', { params });
    return response.data;
  },

  /**
   * Obtener calendario completo de un año
   * @param {number} anio - Año a consultar
   * @returns {Promise} Calendario del año con todos los días y feriados
   */
  getAnio: async (anio) => {
    const response = await api.get(`/calendar/anio/${anio}`);
    return response.data;
  }
};

/**
 * Tipos de feriados disponibles
 */
export const TIPOS_FERIADOS = [
  { value: 'FERIADO_NACIONAL', label: 'Feriado Nacional', color: '#EF4444' },
  { value: 'FERIADO_REGIONAL', label: 'Feriado Regional', color: '#F97316' },
  { value: 'FERIADO_EMPRESA', label: 'Feriado de Empresa', color: '#3B82F6' },
  { value: 'NO_LABORABLE', label: 'Día No Laborable', color: '#6366F1' },
  { value: 'FERIADO_TRABAJABLE', label: 'Feriado Trabajable', color: '#F59E0B' },
  { value: 'MEDIO_DIA', label: 'Medio Día', color: '#8B5CF6' }
];

/**
 * Helper para obtener configuración de tipo de feriado
 * @param {string} tipo - Código del tipo
 * @returns {Object} Configuración del tipo
 */
export const getTipoFeriadoConfig = (tipo) => {
  return TIPOS_FERIADOS.find(t => t.value === tipo) || {
    value: tipo,
    label: tipo,
    color: '#6B7280'
  };
};

export default calendarAPI;
